/**
 * Flora Theme
 * Dark mode theme inspired by flora.ai
 * Primary: Vibrant Green (#71D083), Dark backgrounds
 * Optimized for dark mode with nature-inspired color palette
 */

import { floraColors } from '../tokens/colors.js';
import { spacing, zIndex } from '../tokens/spacing.js';
import { typography } from '../tokens/typography.js';
import { borderRadius, shadowsDark, transitions } from '../tokens/effects.js';

export const floraTheme = {
  name: 'flora',
  label: 'Flora',
  description: 'Dark theme inspired by flora.ai with vibrant green accents',
  isDark: true,

  // Color palette
  colors: {
    primary: floraColors.primary,
    primaryLight: floraColors.primaryLight,
    primaryDark: floraColors.primaryDark,
    primaryHover: floraColors.primaryDark,
    primaryOpacity: floraColors.primaryOpacity20,

    secondary: floraColors.secondary,
    secondaryLight: floraColors.secondaryLight,
    secondaryDark: floraColors.secondaryDark,
    secondaryOpacity: floraColors.secondaryOpacity20,

    tertiary: floraColors.tertiary,
    tertiaryLight: floraColors.tertiaryLight,
    tertiaryDark: floraColors.tertiaryDark,
    tertiaryOpacity: floraColors.tertiaryOpacity20,

    accent: floraColors.accent,
    accentLight: floraColors.accentLight,
    accentDark: floraColors.accentDark,
    accentOpacity: floraColors.accentOpacity20,

    // Semantic colors
    error: floraColors.error,
    errorLight: floraColors.errorLight,
    success: floraColors.primary, // Use green as success in Flora theme
    successLight: 'rgba(113, 208, 131, 0.15)',
    warning: floraColors.warning,
    warningLight: floraColors.warningLight,
    info: floraColors.info,
    infoLight: floraColors.infoLight,

    // Neutrals - Dark mode optimized
    background: floraColors.backgroundDark,
    backgroundAlt: floraColors.backgroundLight,
    backgroundDark: floraColors.backgroundDark,
    backgroundNeutral: '#2A2A2A',
    surface: floraColors.surfaceLight,
    surfaceElevated: floraColors.surfaceElevated,

    // Text
    textPrimary: floraColors.neutral100,
    textSecondary: floraColors.neutral300,
    textTertiary: floraColors.neutral400,
    textInverse: floraColors.white,

    // Borders
    borderDefault: floraColors.neutral500,
    borderSubtle: floraColors.neutral600,
    borderInput: floraColors.borderInput,
  },

  // Spacing
  spacing,

  // Z-index
  zIndex,

  // Typography
  typography,

  // Border radius
  borderRadius,

  // Shadows - Dark mode optimized
  shadows: shadowsDark,

  // Transitions
  transitions,

  // Component-specific tokens
  components: {
    button: {
      paddingX: spacing.base,
      paddingY: '10px',
      borderRadius: borderRadius.md,
      transition: transitions.default,
      fontSize: typography.fontSize.body,
      fontWeight: typography.fontWeight.semibold,
      // SYN-2738 (A9): fundos/texto do DSButton por tema (--button-*).
      primaryBg: floraColors.primaryButton,
      primaryBgHover: floraColors.primaryDark,
      // C12 (2026-10-05): texto do botão ghost/de texto. Aqui = a cor primária de sempre.
      ghostText: floraColors.primary,
      secondaryBg: floraColors.secondaryButton,
      secondaryBgHover: floraColors.secondaryDark,
      accentText: floraColors.backgroundDark,
    },
    card: {
      padding: spacing.xl,
      borderRadius: borderRadius.lg,
      backgroundColor: floraColors.surfaceLight,
      boxShadow: shadowsDark.base,
    },
    input: {
      paddingX: spacing.base,
      paddingY: '10px',
      borderRadius: borderRadius.md,
      borderColor: floraColors.neutral500,
      focusBorderColor: floraColors.primary,
      fontSize: typography.fontSize.body,
    },
    chip: {
      paddingX: spacing.md,
      paddingY: spacing.xs,
      borderRadius: borderRadius.full,
      fontSize: typography.fontSize.labelSmall,
      fontWeight: typography.fontWeight.medium,
    },
    badge: {
      paddingX: spacing.sm,
      paddingY: spacing.xs,
      borderRadius: borderRadius.sm,
      fontSize: typography.fontSize.labelSmall,
      fontWeight: typography.fontWeight.bold,
    },
    sidebar: {
      backgroundColor: floraColors.backgroundLight,
      iconSelected: floraColors.primary,
      iconDefault: floraColors.neutral400,
      iconDisabled: floraColors.neutral600,
      iconStatic: floraColors.neutral500,
      divider: floraColors.neutral600,
    },
  },

  // Vuetify theme configuration
  vuetifyTheme: {
    dark: true,
    colors: {
      primary: floraColors.primary,
      secondary: floraColors.secondary,
      accent: floraColors.accent,
      error: floraColors.error,
      warning: floraColors.warning,
      info: floraColors.info,
      success: floraColors.primary,
      background: floraColors.backgroundLight,
      surface: floraColors.surfaceLight,
    },
  },
};
